/**
 * Word Collection Zustand Store
 *
 * State manager for word collections with optimistic updates, background sync,
 * and retry/rollback capabilities. Keeps UI snappy while persisting changes.
 *
 * Scope 06: Rhyme state removed. Collection is flat words only — the rhyme
 * grouping engine (Scope 04) handles visual grouping by phonetic signature.
 * Backend rhyme endpoints still exist; Scope 07 removes those.
 */

import { create } from 'zustand'
import { backendApiClient } from '../lib/backendApi'
import type { Word } from '../types/wordCollection'

// ============================================================================
// MODULE-LEVEL GUARDS (Synchronous - prevents race conditions)
// ============================================================================

// Track words currently being deleted to prevent duplicate operations
// This is checked synchronously BEFORE any async state updates
const deletingWordIds = new Set<string>()

// Export for test cleanup
export const _resetDeletingWordIds = () => deletingWordIds.clear()

// ============================================================================
// STATE TYPES
// ============================================================================

interface PendingOperation {
  type: 'addWord' | 'updateWord' | 'deleteWord'
  payload: unknown
  timestamp: number
}

interface StateSnapshot {
  words: Word[]
  timestamp: number
}

interface WordCollectionState {
  // Core data
  words: Word[]
  currentSongId: string | null

  // UI state
  searchQuery: string
  usedWordsInLyrics: Set<string>

  // Sync state
  isSyncing: boolean
  lastSyncAt: Date | null
  syncError: string | null

  // Loading state
  isLoading: boolean
  loadError: string | null

  // Internal sync management
  pendingOperations: PendingOperation[]
  syncTimeoutId: NodeJS.Timeout | null
  snapshot: StateSnapshot | null
  retryCount: number
}

interface WordCollectionActions {
  // Load collection from backend
  loadCollection: (songId: string) => Promise<void>

  // Word operations (optimistic)
  addWord: (word: Word) => void
  updateWord: (wordId: string, updates: Partial<Word>) => void
  deleteWord: (wordId: string) => void
  changeWordStatus: (wordId: string, newStatus: Word['status'], reason?: string) => void
  updateWordNotes: (wordId: string, notes: string) => void

  // UI state actions
  setSearchQuery: (query: string) => void
  markWordUsed: (word: string, isUsed: boolean) => void
  syncWithLyrics: (lyricsContent: string) => void
  setUsedWords: (usedWords: Set<string>) => void

  // Sync management
  saveCollection: () => Promise<void>
  _queueSync: () => void
  _createSnapshot: () => void
  _rollbackToSnapshot: () => void

  // Derived selectors
  getCollectedWords: () => Word[]
  getSetAsideWords: () => Word[]
  getWordById: (wordId: string) => Word | undefined
}

type WordCollectionStore = WordCollectionState & WordCollectionActions

// ============================================================================
// CONSTANTS
// ============================================================================

const SYNC_DEBOUNCE_MS = 2000
const MAX_RETRIES = 3

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useWordCollectionStore = create<WordCollectionStore>((set, get) => ({
  // Initial state
  words: [],
  currentSongId: null,
  searchQuery: '',
  usedWordsInLyrics: new Set(),
  isSyncing: false,
  lastSyncAt: null,
  syncError: null,
  isLoading: false,
  loadError: null,
  pendingOperations: [],
  syncTimeoutId: null,
  snapshot: null,
  retryCount: 0,

  // ============================================================================
  // LOAD COLLECTION
  // ============================================================================

  loadCollection: async (songId: string) => {
    // Clear existing data immediately when switching songs to prevent stale data
    set({
      isLoading: true,
      loadError: null,
      currentSongId: songId,
      words: [],
      usedWordsInLyrics: new Set<string>()
    })

    try {
      // Flat word fetch — no more N+1 rhyme fan-out
      const { words } = await backendApiClient.getWordCollection(songId)

      set({
        words,
        isLoading: false,
        loadError: null,
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load collection'
      set({
        isLoading: false,
        loadError: errorMessage,
      })
    }
  },

  // ============================================================================
  // WORD OPERATIONS
  // ============================================================================

  addWord: (word: Word) => {
    const state = get()

    // Create snapshot before first change
    if (!state.snapshot) {
      get()._createSnapshot()
    }

    // Optimistic update
    set({
      words: [...state.words, word],
      pendingOperations: [
        ...state.pendingOperations,
        { type: 'addWord', payload: word, timestamp: Date.now() }
      ]
    })

    // Queue background sync
    get()._queueSync()
  },

  updateWord: (wordId: string, updates: Partial<Word>) => {
    const state = get()

    // Create snapshot before first change
    if (!state.snapshot) {
      get()._createSnapshot()
    }

    // Optimistic update
    set({
      words: state.words.map(w =>
        w.id === wordId ? { ...w, ...updates } : w
      ),
      pendingOperations: [
        ...state.pendingOperations,
        { type: 'updateWord', payload: { wordId, updates }, timestamp: Date.now() }
      ]
    })

    // Queue background sync
    get()._queueSync()
  },

  deleteWord: (wordId: string) => {
    // CRITICAL: Synchronous guard MUST be first - checked before any async state access
    // This prevents race conditions when multiple components call deleteWord simultaneously
    if (deletingWordIds.has(wordId)) {
      return
    }
    // Immediately mark as deleting - this is synchronous and blocks other callers
    deletingWordIds.add(wordId)

    const state = get()

    // Guard: Don't delete if word doesn't exist locally
    if (!state.words.some(w => w.id === wordId)) {
      deletingWordIds.delete(wordId) // Clean up guard
      return
    }

    // Create snapshot before first change
    if (!state.snapshot) {
      get()._createSnapshot()
    }

    // Optimistic update
    set({
      words: state.words.filter(w => w.id !== wordId),
      pendingOperations: [
        ...state.pendingOperations,
        { type: 'deleteWord', payload: { wordId }, timestamp: Date.now() }
      ]
    })

    // Queue background sync
    get()._queueSync()
  },

  changeWordStatus: (wordId: string, newStatus: Word['status'], reason?: string) => {
    const state = get()
    const word = state.words.find(w => w.id === wordId)

    if (!word) return

    // Create snapshot before first change
    if (!state.snapshot) {
      get()._createSnapshot()
    }

    const now = new Date()
    const updates: Partial<Word> = { status: newStatus }

    // Update timeline based on status change
    if (newStatus === 'collected') {
      // Track when word re-enters active use; also clear any set_aside metadata
      updates.timeline = {
        ...word.timeline,
        last_active_at: now,
        set_aside_at: undefined,
        set_aside_reason: undefined
      }
    } else if (newStatus === 'set_aside') {
      updates.timeline = {
        ...word.timeline,
        set_aside_at: now,
        set_aside_reason: reason
      }
    }

    // Use existing updateWord logic
    get().updateWord(wordId, updates)
  },

  updateWordNotes: (wordId: string, notes: string) => {
    const state = get()

    // Create snapshot before first change
    if (!state.snapshot) {
      get()._createSnapshot()
    }

    // Use existing updateWord logic
    get().updateWord(wordId, { notes })
  },

  // ============================================================================
  // UI STATE ACTIONS
  // ============================================================================

  setSearchQuery: (query: string) => {
    set({ searchQuery: query })
  },

  markWordUsed: (word: string, isUsed: boolean) => {
    const state = get()
    const newUsed = new Set(state.usedWordsInLyrics)

    if (isUsed) {
      newUsed.add(word)
    } else {
      newUsed.delete(word)
    }

    set({ usedWordsInLyrics: newUsed })
  },

  syncWithLyrics: (lyricsContent: string) => {
    const state = get()

    // Parse lyrics text to extract words (case-insensitive matching)
    const lyricsWords = new Set(
      lyricsContent
        .toLowerCase()
        .split(/\s+/)
        .filter(w => w.length > 0)
    )

    // Check which collection words appear in lyrics
    const used = new Set<string>()
    for (const word of state.words) {
      if (lyricsWords.has(word.word.toLowerCase())) {
        used.add(word.word)
      }
    }

    set({ usedWordsInLyrics: used })
  },

  setUsedWords: (usedWords: Set<string>) => {
    // Atomic replacement of usedWordsInLyrics
    set({ usedWordsInLyrics: new Set(usedWords) })
  },

  // ============================================================================
  // SYNC MANAGEMENT
  // ============================================================================

  _queueSync: () => {
    const state = get()

    // Clear existing timeout if any
    if (state.syncTimeoutId) {
      clearTimeout(state.syncTimeoutId)
    }

    // Queue new sync after debounce
    const timeoutId = setTimeout(() => {
      // Guard: Only proceed if THIS timeout is still the current one
      // This prevents stale timeouts from firing after being "cleared"
      // (clearTimeout doesn't cancel already-executing callbacks)
      const currentState = get()
      if (currentState.syncTimeoutId !== timeoutId) {
        return
      }
      get().saveCollection()
    }, SYNC_DEBOUNCE_MS)

    set({ syncTimeoutId: timeoutId })
  },

  saveCollection: async () => {
    const state = get()

    // Don't sync if already syncing (serialize requests)
    if (state.isSyncing) {
      return
    }

    // Nothing to sync
    if (state.pendingOperations.length === 0) {
      return
    }

    // Must have a current song
    if (!state.currentSongId) {
      return
    }

    set({ isSyncing: true, syncError: null })

    // Process operations one at a time, removing each as it succeeds
    // This prevents retries from re-running successful operations
    let remainingOps = [...state.pendingOperations]

    // Capture the ops we're about to process - these exact ops will be removed on success
    const opsToProcess = [...state.pendingOperations]
    let lastError: Error | null = null

    for (const op of opsToProcess) {
      try {
        switch (op.type) {
          case 'addWord': {
            const word = op.payload as Word
            await backendApiClient.addWord(state.currentSongId, word)
            break
          }
          case 'updateWord': {
            const { wordId, updates } = op.payload as { wordId: string; updates: Partial<Word> }
            await backendApiClient.updateWord(state.currentSongId, wordId, updates)
            break
          }
          case 'deleteWord': {
            const { wordId } = op.payload as { wordId: string }
            try {
              await backendApiClient.deleteWord(state.currentSongId, wordId)
            } catch (deleteError) {
              // 404 on DELETE = word already gone = success (idempotent delete)
              const msg = deleteError instanceof Error ? deleteError.message : ''
              if (!msg.includes('404')) {
                throw deleteError
              }
              // 404 is fine - word doesn't exist, which is what we wanted
            }
            break
          }
        }

        // Operation succeeded - remove THIS SPECIFIC OP from current store state
        // Critical: use get() to read CURRENT state, not captured state
        // This preserves any ops that were added DURING sync
        const currentOps = get().pendingOperations
        const updatedOps = currentOps.filter(o =>
          !(o.type === op.type && o.timestamp === op.timestamp)
        )
        remainingOps = remainingOps.filter(o => o !== op)
        set({ pendingOperations: updatedOps })

      } catch (error) {
        // Operation failed - stop processing, remaining ops will be retried
        lastError = error instanceof Error ? error : new Error('Unknown sync error')
        break
      }
    }

    // Check if all OUR operations completed (ops we started with)
    if (remainingOps.length === 0) {
      // All ops we were processing succeeded
      // Don't blindly clear - check if new ops were added during sync
      const currentOps = get().pendingOperations
      deletingWordIds.clear()

      if (currentOps.length === 0) {
        // No new ops added during sync - full success
        set({
          isSyncing: false,
          lastSyncAt: new Date(),
          syncError: null,
          snapshot: null,
          retryCount: 0,
        })
      } else {
        // New ops were added during sync - mark as not syncing and let next sync handle them
        set({
          isSyncing: false,
          lastSyncAt: new Date(),
          syncError: null,
          retryCount: 0,
        })
        // Queue another sync for the new ops
        get()._queueSync()
      }
      return
    }

    // Some operations failed - handle retry logic
    const errorMessage = lastError?.message || 'Sync failed'
    const newRetryCount = state.retryCount + 1

    if (newRetryCount >= MAX_RETRIES) {
      // Max retries exceeded - rollback to snapshot and clear guards
      console.error('Max retries exceeded, rolling back to snapshot')
      deletingWordIds.clear()
      get()._rollbackToSnapshot()
      set({
        isSyncing: false,
        syncError: `Failed to sync after ${MAX_RETRIES} attempts: ${errorMessage}`,
        retryCount: 0,
      })
    } else {
      // Retry after exponential backoff (only remaining ops will be retried)
      const backoffMs = Math.pow(2, newRetryCount) * 1000
      console.warn(`Sync failed, retrying in ${backoffMs}ms (attempt ${newRetryCount}/${MAX_RETRIES})`)

      set({
        isSyncing: false,
        syncError: null,
        retryCount: newRetryCount,
      })

      setTimeout(() => {
        get().saveCollection()
      }, backoffMs)
    }
  },

  _createSnapshot: () => {
    const state = get()
    set({
      snapshot: {
        words: [...state.words],
        timestamp: Date.now(),
      }
    })
  },

  _rollbackToSnapshot: () => {
    const state = get()
    if (state.snapshot) {
      set({
        words: state.snapshot.words,
        pendingOperations: [],
        snapshot: null,
      })
    }
  },

  // ============================================================================
  // DERIVED SELECTORS
  // ============================================================================

  getCollectedWords: () => {
    const state = get()
    return state.words.filter(w => w.status === 'collected')
  },

  getSetAsideWords: () => {
    const state = get()
    return state.words.filter(w => w.status === 'set_aside')
  },

  getWordById: (wordId: string) => {
    const state = get()
    return state.words.find(w => w.id === wordId)
  },
}))
